import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, useMap, useMapEvents } from 'react-leaflet';
import polyline from '@mapbox/polyline';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';

function ZoomWithFullscreenControl() {
  const map = useMap();
  useEffect(() => {
    const ZoomWithFullscreen = L.Control.extend({
      onAdd() {
        const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
        div.innerHTML = `
          <a class="leaflet-control-zoom-in" href="#" title="Zoom in" role="button" aria-label="Zoom in">+</a>
          <a class="leaflet-control-fullscreen" href="#" title="Fullscreen" role="button" aria-label="Fullscreen">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
            </svg>
          </a>
          <a class="leaflet-control-zoom-out" href="#" title="Zoom out" role="button" aria-label="Zoom out">−</a>
        `;
        div.querySelector('.leaflet-control-zoom-in').onclick = (e) => {
          L.DomEvent.preventDefault(e);
          map.zoomIn(0.25);
        };
        div.querySelector('.leaflet-control-zoom-out').onclick = (e) => {
          L.DomEvent.preventDefault(e);
          map.zoomOut(0.25);
        };
        div.querySelector('.leaflet-control-fullscreen').onclick = (e) => {
          L.DomEvent.preventDefault(e);
          const container = map.getContainer();
          if (!document.fullscreenElement) {
            container.requestFullscreen?.();
          } else {
            document.exitFullscreen?.();
          }
        };
        L.DomEvent.disableClickPropagation(div);
        return div;
      },
    });
    const control = new ZoomWithFullscreen({ position: 'topleft' });
    map.addControl(control);
    return () => map.removeControl(control);
  }, [map]);
  return null;
}

const goalIcon = L.divIcon({
  html: '<div style="display:flex;align-items:center;justify-content:center;width:16px;height:16px;background:#fff;border:2px solid #ef4444;border-radius:50%;box-shadow:0 1px 2px rgba(0,0,0,0.3);font-size:9px;line-height:1;padding:0;">🏁</div>',
  className: 'leaflet-div-icon goal-marker',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Pace (sec/km) to heat-cool color: 3:00/km = max heat (red), 7:00/km = max cool (blue)
const PACE_HEAT = 180;   // 3:00/km
const PACE_COOL = 420;   // 7:00/km
function paceToColor(paceSec) {
  if (paceSec == null || paceSec <= 0) return '#94a3b8';
  const t = Math.max(0, Math.min(1, (paceSec - PACE_HEAT) / (PACE_COOL - PACE_HEAT)));
  const hue = t * 240;
  return `hsl(${hue}, 85%, 45%)`;
}

function createKmMarkerIcon(km, highlighted) {
  const border = highlighted ? '3px solid #f59e0b' : '2px solid #3b82f6';
  const size = highlighted ? 26 : 22;
  const fontSize = highlighted ? 12 : 11;
  const shadow = highlighted ? '0 0 0 2px rgba(245,158,11,0.35),0 2px 6px rgba(0,0,0,0.35)' : '0 1px 3px rgba(0,0,0,0.25)';
  const anchor = size / 2;
  return L.divIcon({
    html: `<div style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;background:#fff;border:${border};border-radius:50%;box-shadow:${shadow};font-size:${fontSize}px;font-weight:700;color:#1e40af;line-height:1;">${km}</div>`,
    className: 'leaflet-div-icon km-marker',
    iconSize: [size, size],
    iconAnchor: [anchor, anchor],
  });
}

function FitBounds({ bounds, padding = [40, 40], maxZoom }) {
  const map = useMap();
  const paddingRef = useRef(padding);
  paddingRef.current = padding;
  const maxZoomRef = useRef(maxZoom);
  maxZoomRef.current = maxZoom;

  const fit = useCallback(() => {
    if (bounds && bounds.length > 0) {
      const opts = { padding: paddingRef.current };
      if (maxZoomRef.current != null) {
        opts.maxZoom = maxZoomRef.current;
      }
      map.fitBounds(bounds, opts);
    }
  }, [map, bounds]);

  useEffect(() => {
    fit();
    const onResize = () => fit();
    map.on('resize', onResize);
    return () => map.off('resize', onResize);
  }, [map, fit]);

  useEffect(() => {
    const container = map.getContainer();
    const parent = container.parentElement;
    if (!parent) return undefined;
    const invalidate = () => {
      map.invalidateSize({ pan: false });
    };
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(invalidate);
    });
    ro.observe(parent);
    map.whenReady(() => {
      requestAnimationFrame(() => requestAnimationFrame(invalidate));
    });
    return () => ro.disconnect();
  }, [map]);

  return null;
}

function isMapContainerFullscreen(map) {
  const el = document.fullscreenElement ?? document.webkitFullscreenElement;
  return !!(el && el === map.getContainer());
}

function KmMarkersLayer({ kmMarkers, coords, highlightKmSet, onKmMarkerHover }) {
  const map = useMap();
  const [showMarkers, setShowMarkers] = useState(false);

  const checkCoverage = useCallback(() => {
    if (!coords || coords.length === 0) {
      setShowMarkers(false);
      return;
    }
    const routeBounds = L.latLngBounds(coords);
    const mapBounds = map.getBounds();
    const routeW = routeBounds.getEast() - routeBounds.getWest();
    const routeH = routeBounds.getNorth() - routeBounds.getSouth();
    const mapW = mapBounds.getEast() - mapBounds.getWest();
    const mapH = mapBounds.getNorth() - mapBounds.getSouth();
    const threshold = isMapContainerFullscreen(map) ? 0.5 : 0.7;
    const coversWidth = mapW > 0 && routeW / mapW >= threshold;
    const coversHeight = mapH > 0 && routeH / mapH >= threshold;
    setShowMarkers(coversWidth || coversHeight);
  }, [map, coords]);

  useMapEvents({
    moveend: checkCoverage,
    zoomend: checkCoverage,
  });

  useEffect(() => {
    map.whenReady(checkCoverage);
  }, [map, checkCoverage]);

  useEffect(() => {
    const onFullscreenChange = () => checkCoverage();
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, [checkCoverage]);

  if (!showMarkers || !kmMarkers.length) return null;

  return (
    <>
      {kmMarkers.map((m) => {
        const hi = highlightKmSet?.has?.(m.km);
        return (
          <Marker
            key={`km-${m.km}-${hi ? '1' : '0'}`}
            position={m.position}
            icon={createKmMarkerIcon(m.km, hi)}
            zIndexOffset={hi ? 450 : 400}
            eventHandlers={{
              mouseover: () => onKmMarkerHover?.(m.km),
              mouseout: () => onKmMarkerHover?.(null),
            }}
          />
        );
      })}
    </>
  );
}

const ActivityMap = ({
  encodedPolyline,
  summaryPolyline,
  streams,
  className = '',
  height = 400,
  fillHeight = false,
  fitPadding,
  fitBoundsMaxZoom,
  highlightKmSet,
  onKmMarkerHover,
}) => {
  const { mode } = useTheme();
  const tileUrl = mode === 'dark'
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  const { coords, coloredSegments, kmMarkers } = useMemo(() => {
    const encoded = encodedPolyline || summaryPolyline;
    let baseCoords = [];
    if (encoded) {
      try {
        baseCoords = polyline.decode(encoded);
      } catch {
        baseCoords = [];
      }
    }

    const latlng = streams?.latlng?.data;
    const distance = streams?.distance?.data;
    const velocity = streams?.velocity_smooth?.data;

    const hasStreams = Array.isArray(latlng) && latlng.length > 1 && Array.isArray(distance) && distance.length > 0;

    if (!hasStreams) {
      return {
        coords: baseCoords,
        coloredSegments: null,
        kmMarkers: [],
      };
    }

    const len = Math.min(latlng.length, distance.length);
    const paceSec = velocity?.length
      ? velocity.map((v) => (v && v > 0.2 ? 1000 / v : null))
      : null;

    const merged = [];
    let current = null;
    for (let i = 0; i < len - 1; i++) {
      const a = latlng[i];
      const b = latlng[i + 1];
      if (!a || !b || !Array.isArray(a) || !Array.isArray(b)) continue;
      const posA = [a[0], a[1]];
      const posB = [b[0], b[1]];
      const pace = paceSec?.[i] ?? paceSec?.[i + 1];
      const color = paceToColor(pace);
      if (current && current.color === color) {
        current.positions.push(posB);
      } else {
        current = { positions: [posA, posB], color };
        merged.push(current);
      }
    }
    const segments = merged;

    const markers = [];
    let nextKm = 1000;
    for (let i = 1; i < len; i++) {
      const d0 = parseFloat(distance[i - 1]) || 0;
      const d1 = parseFloat(distance[i]) || 0;
      if (d1 >= nextKm && d0 < nextKm) {
        const t = (nextKm - d0) / (d1 - d0 || 1);
        const lat = latlng[i - 1][0] + t * (latlng[i][0] - latlng[i - 1][0]);
        const lng = latlng[i - 1][1] + t * (latlng[i][1] - latlng[i - 1][1]);
        markers.push({ position: [lat, lng], km: nextKm / 1000 });
        nextKm += 1000;
      }
    }

    return {
      coords: latlng.map((p) => (Array.isArray(p) ? [p[0], p[1]] : null)).filter(Boolean),
      coloredSegments: segments,
      kmMarkers: markers,
    };
  }, [encodedPolyline, summaryPolyline, streams]);

  if (coords.length === 0) return null;

  const startPoint = coords[0];
  const endPoint = coords[coords.length - 1];
  const useColoredLine = coloredSegments && coloredSegments.length > 0;

  return (
    <div
      className={cn(
        'min-w-0 w-full overflow-hidden rounded-xl border border-border',
        fillHeight ? 'h-full min-h-[220px]' : '',
        className
      )}
      style={fillHeight ? undefined : { height }}
    >
      <MapContainer
        center={startPoint}
        zoom={13}
        zoomSnap={0.25}
        zoomDelta={0.25}
        className='!size-full z-0'
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        scrollWheelZoom={true}
      >
        <ZoomWithFullscreenControl />
        <TileLayer
          key={mode}
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url={tileUrl}
        />
        <FitBounds bounds={coords} padding={fitPadding} maxZoom={fitBoundsMaxZoom} />

        {useColoredLine ? (
          <>
            <Polyline
              positions={coords}
              pathOptions={{
                color: mode === 'dark' ? '#1e293b' : '#e2e8f0',
                weight: 6,
                opacity: 0.5,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
            {coloredSegments.map((seg, idx) => (
              <Polyline
                key={idx}
                positions={seg.positions}
                pathOptions={{
                  color: seg.color,
                  weight: 4,
                  opacity: 0.95,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
              />
            ))}
          </>
        ) : (
          <>
            <Polyline
              positions={coords}
              pathOptions={{
                color: '#3b82f6',
                weight: 4,
                opacity: 0.9,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
            <Polyline
              positions={coords}
              pathOptions={{
                color: '#3b82f6',
                weight: 10,
                opacity: 0.15,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            />
          </>
        )}

        <KmMarkersLayer
          kmMarkers={kmMarkers}
          coords={coords}
          highlightKmSet={highlightKmSet}
          onKmMarkerHover={onKmMarkerHover}
        />

        <CircleMarker
          center={startPoint}
          radius={8}
          pathOptions={{
            color: '#ffffff',
            weight: 2,
            fillColor: '#22c55e',
            fillOpacity: 1,
          }}
        />

        <Marker position={endPoint} icon={goalIcon} zIndexOffset={500} />
      </MapContainer>
    </div>
  );
};

export default ActivityMap;
